import { Grid, Divider, TextField, Autocomplete, CircularProgress, FormControlLabel, Switch, MenuItem } from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAccountById, updateAccount, fetchBuyerOrSupplierByName, fetchAccountSettings, updateAccountSettings } from "../../store/accountThunk";

// WhatsApp notification toggles shown in the Settings tab (keys must match the backend model).
const WHATSAPP_NOTIFICATION_OPTIONS: { key: string; label: string }[] = [
  { key: 'invoiceCreated', label: 'Invoice created' },
  { key: 'paymentReceived', label: 'Payment received' },
  { key: 'paymentDueReminder', label: 'Payment due reminder' },
  { key: 'paymentOverdue', label: 'Payment overdue alert (the day after due date)' },
  { key: 'businessSummary', label: 'Daily business summary' },
];

// Email notification toggles (keys must match the backend emailNotifications model).
const EMAIL_NOTIFICATION_OPTIONS: { key: string; label: string }[] = [
  { key: 'businessSummary', label: 'Daily business summary' },
];

// Selectable "days before due date" values for the payment due reminder.
const DUE_REMINDER_DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 10, 15];
const DEFAULT_DUE_REMINDER_DAYS = 2;

// Which card group to render. The Settings page tabs one section in at a time.
export type SettingsSection = 'notifications' | 'cashSale';

interface SettingsProps {
  section: SettingsSection;
}

const Settings: FC<SettingsProps> = ({ section }) => {
  const dispatch = useAppDispatch();

  const showNotifications = section === 'notifications';
  const showCashSale = section === 'cashSale';

  // Cash sale account state
  const [options, setOptions] = useState<any[]>([]);
  const [selectedCashSale, setSelectedCashSale] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [savingCashSale, setSavingCashSale] = useState(false);

  // WhatsApp notification toggles
  const [waNotifications, setWaNotifications] = useState<any>(null);
  const [savingWaKey, setSavingWaKey] = useState<string | null>(null);

  // Email notification toggles
  const [emailNotifications, setEmailNotifications] = useState<any>(null);
  const [savingEmailKey, setSavingEmailKey] = useState<string | null>(null);

  // How many days before the due date the payment due reminder is sent.
  const [dueReminderDays, setDueReminderDays] = useState<number>(DEFAULT_DUE_REMINDER_DAYS);
  const [savingDueReminderDays, setSavingDueReminderDays] = useState(false);

  const accountDetails = useAppSelector(state => state.account.accountDetails);

  // Debounce search input
  useEffect(() => {
    if (!showCashSale) return;
    setIsSearching(true);
    const timerId = setTimeout(() => {
      setDebouncedTerm(searchTerm);
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm, showCashSale]);

  // Fetch buyers when debounced term changes
  useEffect(() => {
    if (!accountDetails?._id || !showCashSale) return;
    setIsSearching(true);
    const fetchData = async () => {
      try {
        const queryString = `name=${debouncedTerm}&&searchFor=buyer`;
        const data: any = await dispatch(fetchBuyerOrSupplierByName(queryString)).unwrap();
        if (data) {
          const filtered = data.accounts.filter((item: any) => item._id !== accountDetails._id);
          setOptions(debouncedTerm ? filtered : filtered.slice(0, 5));
        }
      } catch (error) {
        console.error("Error fetching buyers:", error);
      }
      setIsSearching(false);
    };
    fetchData();
  }, [debouncedTerm]);

  // Handle buyer selection
  const handleCashSaleChange = async (_event: any, value: any) => {
    setSelectedCashSale(value);
    setSavingCashSale(true);
    try {
      const formData = new FormData();
      formData.append('defaultCashSaleAccount', value?._id || '');
      const responseData: any = await dispatch(updateAccount({
        id: accountDetails._id,
        accountPayload: formData as any,
      }));
      if (responseData && responseData?.status === 200) {
        dispatch(fetchAccountById(accountDetails._id));
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setSavingCashSale(false);
    }
  };

  // Load WhatsApp notification settings when the account is available.
  useEffect(() => {
    if (!accountDetails?._id || !showNotifications) return;
    const loadSettings = async () => {
      try {
        const res: any = await dispatch(fetchAccountSettings(accountDetails._id)).unwrap();
        if (res?.settings?.whatsappNotifications) {
          setWaNotifications(res.settings.whatsappNotifications);
        }
        if (res?.settings?.emailNotifications) {
          setEmailNotifications(res.settings.emailNotifications);
        }
        if (res?.settings?.paymentDueReminderDays) {
          setDueReminderDays(res.settings.paymentDueReminderDays);
        }
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
    };
    loadSettings();
  }, [accountDetails?._id, showNotifications]);

  // Toggle a single email notification type and persist it.
  const handleEmailToggle = async (key: string, checked: boolean) => {
    const previous = emailNotifications;
    setEmailNotifications({ ...(emailNotifications || {}), [key]: checked }); // optimistic
    setSavingEmailKey(key);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        emailNotifications: { [key]: checked },
      })).unwrap();
      if (res?.settings?.emailNotifications) {
        setEmailNotifications(res.settings.emailNotifications);
      }
    } catch (error) {
      console.error('Error updating email notification setting:', error);
      setEmailNotifications(previous); // revert on failure
    } finally {
      setSavingEmailKey(null);
    }
  };

  // Toggle a single WhatsApp notification type and persist it.
  const handleWaToggle = async (key: string, checked: boolean) => {
    const previous = waNotifications;
    setWaNotifications({ ...(waNotifications || {}), [key]: checked }); // optimistic
    setSavingWaKey(key);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        whatsappNotifications: { [key]: checked },
      })).unwrap();
      if (res?.settings?.whatsappNotifications) {
        setWaNotifications(res.settings.whatsappNotifications);
      }
    } catch (error) {
      console.error('Error updating WhatsApp notification setting:', error);
      setWaNotifications(previous); // revert on failure
    } finally {
      setSavingWaKey(null);
    }
  };

  // Change how many days before the due date the payment due reminder fires, and persist it.
  const handleDueReminderDaysChange = async (days: number) => {
    const previous = dueReminderDays;
    setDueReminderDays(days); // optimistic
    setSavingDueReminderDays(true);
    try {
      const res: any = await dispatch(updateAccountSettings({
        accountId: accountDetails._id,
        paymentDueReminderDays: days,
      })).unwrap();
      if (res?.settings?.paymentDueReminderDays) {
        setDueReminderDays(res.settings.paymentDueReminderDays);
      }
    } catch (error) {
      console.error('Error updating payment due reminder days:', error);
      setDueReminderDays(previous); // revert on failure
    } finally {
      setSavingDueReminderDays(false);
    }
  };

  // Sync selected value from account details
  useEffect(() => {
    let active = true;
    const settingsInfo = () => {
      try {
        if (active) {
          if (accountDetails.defaultCashSaleAccount && typeof accountDetails.defaultCashSaleAccount === 'object') {
            setSelectedCashSale(accountDetails.defaultCashSaleAccount);
          } else {
            setSelectedCashSale(null);
          }
        }
      } catch (error: any) {
        console.log('Setting catch block called');
      }
    };
    settingsInfo();
    return () => { active = false; };
  }, [accountDetails]);

  return (
    <Grid container spacing={2} alignItems="stretch">
      {/* Notifications tab, left column: WhatsApp Notifications */}
      {showNotifications && <Grid item xs={12} md={6}>
        <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10, height: '100%' }}>
          <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>WhatsApp Notifications</h5>
          <Divider sx={{ mt: 0.5, mb: 1.5 }} />
          <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
            Choose which WhatsApp messages this account sends. Each type can be turned on or off independently.
          </h5>
          <Grid container spacing={1}>
            {WHATSAPP_NOTIFICATION_OPTIONS.map((option) => (
              <Grid item xs={12} key={option.key}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 480 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!waNotifications?.[option.key]}
                        onChange={(e) => handleWaToggle(option.key, e.target.checked)}
                        disabled={waNotifications === null || savingWaKey === option.key}
                        size="small"
                      />
                    }
                    label={<span style={{ fontSize: 14, color: '#000' }}>{option.label}</span>}
                  />
                  {savingWaKey === option.key ? <CircularProgress size={16} /> : null}
                </div>

                {/* Days-before selector for the payment due reminder. Shown only when that type is on. */}
                {option.key === 'paymentDueReminder' && !!waNotifications?.paymentDueReminder ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 46, marginTop: 2, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, color: '#9da7b7' }}>Send</span>
                    <TextField
                      select
                      size="small"
                      variant="standard"
                      value={dueReminderDays}
                      onChange={(e) => handleDueReminderDaysChange(Number(e.target.value))}
                      disabled={savingDueReminderDays}
                      sx={{ width: 64 }}
                      InputProps={{ disableUnderline: false }}
                    >
                      {DUE_REMINDER_DAY_OPTIONS.map((d) => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                      ))}
                    </TextField>
                    <span style={{ fontSize: 13, color: '#9da7b7' }}>day(s) before due date</span>
                    {savingDueReminderDays ? <CircularProgress size={14} /> : null}
                  </div>
                ) : null}
              </Grid>
            ))}
          </Grid>
        </div>
      </Grid>}

      {/* Default Cash Sale Account tab */}
      {showCashSale && <Grid item xs={12} md={6}>
            <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10 }}>
              <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>Default Cash Sale Account</h5>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
              <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
                Select a buyer account to use as the default for cash sales
              </h5>
              <div style={{ display: 'flex', alignItems: 'center', maxWidth: 400 }}>
                <Autocomplete
                  sx={{ width: '100%' }}
                  value={selectedCashSale}
                  onChange={handleCashSaleChange}
                  onInputChange={(_e, value) => setSearchTerm(value)}
                  options={options}
                  getOptionLabel={(option: any) => option.name || ''}
                  isOptionEqualToValue={(option: any, value: any) => option._id === value?._id}
                  loading={isSearching}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Type here to search Buyer'
                      className='c-text-field'
                      variant="standard"
                      size='small'
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        endAdornment: (
                          <>
                            {isSearching || savingCashSale ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option: any) => (
                    <li {...props} key={option._id}>
                      {option.name || option.legalName || option._id}
                    </li>
                  )}
                />
              </div>
            </div>
      </Grid>}

      {/* Notifications tab, right column: Email Notifications */}
      {showNotifications && <Grid item xs={12} md={6}>
            <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10 }}>
              <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>Email Notifications</h5>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
              <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
                Choose which emails this account sends. Each type can be turned on or off independently.
              </h5>
              <Grid container spacing={1}>
                {EMAIL_NOTIFICATION_OPTIONS.map((option) => (
                  <Grid item xs={12} key={option.key}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 480 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!emailNotifications?.[option.key]}
                            onChange={(e) => handleEmailToggle(option.key, e.target.checked)}
                            disabled={emailNotifications === null || savingEmailKey === option.key}
                            size="small"
                          />
                        }
                        label={<span style={{ fontSize: 14, color: '#000' }}>{option.label}</span>}
                      />
                      {savingEmailKey === option.key ? <CircularProgress size={16} /> : null}
                    </div>
                  </Grid>
                ))}
              </Grid>
            </div>
      </Grid>}
    </Grid>
  );
};

export default Settings;
